/**
 * The chart contract, read the way an analyst would read it before drawing.
 *
 * The AI's chart agent writes `{ type: "chart", title, content: { title, pri_x_axis, pri_y_axis,
 * alt_x_axis, alt_y_axis, series: [{ series_type, title, color, features, show_in_legend,
 * x_values, y_values, z_values, color_values }] } }`. Every rule in this file comes from the 816
 * distinct charts stored on staging and production up to 2026-10-15:
 *
 *  - values are kept as written (0.455 stays 0.455, 81.48 stays 81.48); only the ticks and the
 *    labels are rounded, and to the precision of the scale, never the value;
 *  - a numeric string is a number, an empty string / null / -1 / text is a gap, never a zero;
 *  - the axis range starts from the data and honours the spec's min / max / tick interval only
 *    when they contain the data and give a readable number of ticks;
 *  - dates sort as dates whatever their spelling, numbers as numbers, weekdays Monday first,
 *    and every other category keeps the order the AI chose (a "top 10" stays a top 10);
 *  - a chart mixing bars and lines is one composed chart; a chart asked to show "bar, pie, line
 *    and radar views" is one panel per view; a radar with fewer than three axes and a line
 *    with one point are bars, because that is the only honest drawing of them;
 *  - series that share a colour (eleven vehicles all #5E62CC) are told apart with the palette,
 *    and past eight lines the smaller ones go grey so the chart stays readable;
 *  - pie slices walk the app's thirteen colours in their old order, then the five remaining
 *    greens, before repeating; past twelve slices or radar axes the names come off the chart
 *    and stay in the tooltip, as the component always did.
 *
 * `buildChartModel` is pure and never throws on a spec it does not understand: it returns an
 * empty model and the caller shows "No data available".
 */
import { dateLabelKeys, parseDateLabel } from "./DateLabel";

/** The app's palette, in the fixed order series take it: purple, blue, green, then lighter steps. */
export const SERIES_COLORS = [
  "#5E62CC", "#1186E7", "#64B482",
  "#8E91DB", "#58AAEE", "#82C49A",
  "#767AD4", "#3598EB", "#A6A9E3", "#7CBCF2",
  "#BFC0EB", "#A0CFF5", "#D7D8F2", "#C3E1F9",
];

/** The palette families the agent walks for per-slice / per-bar colours. */
export const PALETTE_FAMILIES = [
  ["#5E62CC", "#767AD4", "#8E91DB", "#A6A9E3", "#BFC0EB", "#D7D8F2"],
  ["#1186E7", "#3598EB", "#58AAEE", "#7CBCF2", "#A0CFF5", "#C3E1F9"],
  ["#64B482", "#78C999", "#8DD0A8", "#A3DAB8", "#B9E3C9", "#CFEDDA"],
];

/**
 * Pie slice colours, in the order the app has always walked them (six purples, six blues, the
 * green), followed by the remaining greens of the families, so eighteen slices are told apart
 * before a colour repeats.
 */
export const SLICE_COLORS = [
  "#5E62CC", "#767AD4", "#8E91DB", "#A6A9E3", "#BFC0EB", "#D7D8F2",
  "#1186E7", "#3598EB", "#58AAEE", "#7CBCF2", "#A0CFF5", "#C3E1F9",
  "#64B482", "#78C999", "#8DD0A8", "#A3DAB8", "#B9E3C9", "#CFEDDA",
];

/** Series beyond the readable count are drawn in this grey. */
export const MUTED_COLOR = "#CDD1DB";

/** Past this many pie slices or radar axes the names leave the chart and stay in the tooltip. */
export const MAX_LEGEND_ITEMS = 12;

export const MAX_COLOURED_SERIES = 8;

const CARTESIAN = new Set(["line", "area", "bar", "scatter"]);

const WEEKDAYS = {
  mon: 0, monday: 0, tue: 1, tues: 1, tuesday: 1, wed: 2, wednesday: 2,
  thu: 3, thur: 3, thurs: 3, thursday: 3, fri: 4, friday: 4, sat: 5, saturday: 5, sun: 6, sunday: 6,
};

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTH_INDEX = {
  jan: 0, january: 0, feb: 1, february: 1, mar: 2, march: 2, apr: 3, april: 3, may: 4,
  jun: 5, june: 5, jul: 6, july: 6, aug: 7, august: 7, sep: 8, sept: 8, september: 8,
  oct: 9, october: 9, nov: 10, november: 10, dec: 11, december: 11,
};

const YEAR_MONTH = /^(\d{4})-(\d{2})$/;
const MONTH_YEAR = /^([a-z]{3,9})\.?,?\s+(\d{4})$/i;
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{1,2}):(\d{2}))?/;

/* ----------------------------------------------------------------------------- numbers */

/**
 * A value as a number, or null when it is a gap. "160" is 160, "12.5%" is 12.5, "£81.48" is
 * 81.48, "1,234" is 1234; "", null, -1 (the contract's "no value") and text are gaps.
 */
export function toNumber(value) {
  if (value === null || value === undefined || typeof value === "boolean") return null;
  if (typeof value === "number") return Number.isFinite(value) && value !== -1 ? value : null;
  if (typeof value !== "string") return null;
  const text = value.trim().replace(/[,£$€%\s]/g, "");
  if (text === "" || text === "-" || text === "–") return null;
  if (!/^[-+]?(\d+\.?\d*|\.\d+)(e[-+]?\d+)?$/i.test(text)) return null;
  const number = Number(text);
  return Number.isFinite(number) && number !== -1 ? number : null;
}

const isJsonNumber = (value) => typeof value === "number" && Number.isFinite(value);

/** 1 / 2 / 2.5 / 5 × 10ⁿ, the step that splits `range` into about `target` ticks. */
export function niceStep(range, target = 5) {
  if (!(range > 0)) return 1;
  const raw = range / target;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const ratio = raw / magnitude;
  const factor = ratio <= 1 ? 1 : ratio <= 2 ? 2 : ratio <= 2.5 ? 2.5 : ratio <= 5 ? 5 : 10;
  return factor * magnitude;
}

/** Decimal places a tick needs to show a step of this size (0.1 → 1, 0.025 → 3, 50 → 0). */
export function decimalsForStep(step) {
  if (!(step > 0) || step >= 1) return 0;
  return Math.min(4, Math.ceil(-Math.log10(step) - 1e-9));
}

const round = (value, decimals) => {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
};

/** A tick label: 1,200 · 12.5 · 0.45 · 120K · 1.2M — rounded to the scale's own precision. */
export function formatTick(value, step = 1) {
  if (value === null || value === undefined || !Number.isFinite(value)) return "";
  const abs = Math.abs(value);
  if (abs >= 1e6 && step >= 1e5) return `${trimZeros((value / 1e6).toFixed(1))}M`;
  if (abs >= 1e4 && step >= 1e3) return `${trimZeros((value / 1e3).toFixed(1))}K`;
  const decimals = decimalsForStep(step);
  return round(value, decimals).toLocaleString("en-GB", {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });
}

const trimZeros = (text) => text.replace(/\.0+$/, "").replace(/(\.\d*?)0+$/, "$1");

/**
 * A value as the reader should see it: integers with thousands separators, otherwise two
 * decimals (one above 100), and three significant figures under 1 (0.0458 → 0.046).
 */
export function formatValue(value) {
  if (value === null || value === undefined || !Number.isFinite(value)) return "–";
  const abs = Math.abs(value);
  if (Number.isInteger(value)) return value.toLocaleString("en-GB");
  let decimals;
  if (abs >= 100) decimals = 1;
  else if (abs >= 1) decimals = 2;
  else decimals = Math.min(6, 1 - Math.floor(Math.log10(abs)));
  const rounded = round(value, decimals);
  if (Number.isInteger(rounded)) return rounded.toLocaleString("en-GB");
  return rounded.toLocaleString("en-GB", { minimumFractionDigits: 0, maximumFractionDigits: decimals });
}

/**
 * The unit an axis title carries in its trailing parentheses: "(%)", "(£)", "(miles)",
 * "(points)". Anything with spaces or signs ("lower = safer") is a remark, not a unit.
 */
export function unitFromTitle(title) {
  if (typeof title !== "string") return null;
  const match = title.match(/\(([^()]{1,12})\)\s*$/);
  if (!match) return null;
  const unit = match[1].trim();
  if (unit === "" || /[\s=/:]/.test(unit) || /^\d+$/.test(unit)) return null;
  return unit;
}

/** `value` with its unit: "12.5%", "£81.48", "1,234 miles". */
export function formatWithUnit(value, unit) {
  const text = formatValue(value);
  if (!unit || text === "–") return text;
  if (/^[£$€¥]$/.test(unit)) return value < 0 ? `-${unit}${formatValue(-value)}` : `${unit}${text}`;
  if (unit === "%") return `${text}%`;
  return `${text} ${unit}`;
}

/* ----------------------------------------------------------------------------- scales */

/**
 * The range and ticks of a value axis. Starts at zero for positive data, reaches below zero
 * when the data does, leaves headroom above the top value, and honours the spec's min / max /
 * tick interval when they contain the data and give between 2 and 14 ticks.
 */
export function buildScale(values, spec, options = {}) {
  const numbers = values.filter((v) => v !== null && v !== undefined && Number.isFinite(v));
  const { includeZero = true, targetTicks = 5 } = options;
  let dataMin = numbers.length ? Math.min(...numbers) : 0;
  let dataMax = numbers.length ? Math.max(...numbers) : 0;
  if (!numbers.length) {
    dataMin = 0;
    dataMax = 1;
  }

  let lo = dataMin;
  let hi = dataMax;
  if (includeZero || dataMin >= 0) lo = Math.min(lo, 0);
  if (includeZero || dataMax <= 0) hi = Math.max(hi, 0);

  const specMin = toNumber(spec?.min_value);
  const specMax = toNumber(spec?.max_value);
  const specTick = toNumber(spec?.tick_interval);

  let minHonoured = false;
  let maxHonoured = false;
  if (specMin !== null && specMin <= dataMin && !(dataMin >= 0 && specMin < 0)) {
    lo = specMin;
    minHonoured = true;
  }
  if (specMax !== null && specMax > lo && specMax >= dataMax * 1.02 + (dataMax === 0 ? 1 : 0)) {
    hi = specMax;
    maxHonoured = true;
  }

  if (hi === lo) {
    hi = lo === 0 ? 1 : lo + Math.abs(lo) * 0.1;
  }

  let step = null;
  if (specTick !== null && specTick > 0) {
    const count = (hi - lo) / specTick;
    if (count >= 2 && count <= 14) step = specTick;
  }
  if (step === null) step = niceStep(hi - lo, targetTicks);

  if (!minHonoured) lo = Math.floor(lo / step + 1e-9) * step;
  if (!maxHonoured) {
    const padded = hi > 0 ? hi + (hi - lo) * 0.04 : hi;
    hi = Math.ceil(padded / step - 1e-9) * step;
    if (hi <= dataMax && dataMax > 0) hi += step;
  }

  const decimals = decimalsForStep(step);
  const ticks = [];
  for (let t = lo; t <= hi + step * 1e-6 && ticks.length < 40; t += step) {
    ticks.push(round(t, decimals + 2));
  }
  if (ticks.length && Math.abs(ticks[ticks.length - 1] - hi) > step * 1e-6) ticks.push(round(hi, decimals + 2));

  return {
    min: round(lo, decimals + 2),
    max: round(hi, decimals + 2),
    step,
    ticks,
    decimals,
    format: (v) => formatTick(v, step),
  };
}

/* ----------------------------------------------------------------------------- the x axis */

const monthYearKey = (text) => {
  const match = typeof text === "string" && text.trim().match(MONTH_YEAR);
  if (!match) return null;
  const month = MONTH_INDEX[match[1].toLowerCase()];
  if (month === undefined) return null;
  return Number(match[2]) * 12 + month;
};

const yearMonthKey = (text) => {
  const match = typeof text === "string" && text.trim().match(YEAR_MONTH);
  if (!match) return null;
  const month = Number(match[2]) - 1;
  if (month < 0 || month > 11) return null;
  return Number(match[1]) * 12 + month;
};

const weekdayKey = (text) => {
  if (typeof text !== "string") return null;
  const key = WEEKDAYS[text.trim().toLowerCase().replace(/\.$/, "")];
  return key === undefined ? null : key;
};

/**
 * Reads the distinct x values of a panel and decides what the axis is: numbers, dates (any
 * spelling), year-months, month-years, weekdays, or plain categories in the order given.
 */
export function classifyAxis(rawValues) {
  const seen = new Map();
  rawValues.forEach((value) => {
    if (value === null || value === undefined) return;
    const key = String(value);
    if (!seen.has(key)) seen.set(key, value);
  });
  const keys = Array.from(seen.keys());
  const values = Array.from(seen.values());
  if (keys.length === 0) return { kind: "category", keys: [], labelOf: (k) => k };

  if (values.every(isJsonNumber)) {
    const sorted = [...keys].sort((a, b) => Number(a) - Number(b));
    return { kind: "number", keys: sorted, labelOf: (k) => formatValue(Number(k)), numberOf: (k) => Number(k) };
  }

  const strings = values.every((v) => typeof v === "string");
  if (strings) {
    const ym = values.map(yearMonthKey);
    if (ym.every((k) => k !== null)) {
      const order = keys.map((k, i) => ({ k, key: ym[i] })).sort((a, b) => a.key - b.key).map((e) => e.k);
      return {
        kind: "month",
        keys: order,
        labelOf: (k) => {
          const key = yearMonthKey(k);
          return `${MONTHS_SHORT[key % 12]} ${Math.floor(key / 12)}`;
        },
      };
    }
    const my = values.map(monthYearKey);
    if (my.every((k) => k !== null)) {
      const order = keys.map((k, i) => ({ k, key: my[i] })).sort((a, b) => a.key - b.key).map((e) => e.k);
      return { kind: "month", keys: order, labelOf: (k) => k };
    }
    const dateKeys = dateLabelKeys(values);
    if (dateKeys !== null) {
      const order = keys
        .map((k, i) => ({ k, key: dateKeys[i], i }))
        .sort((a, b) => a.key - b.key || a.i - b.i)
        .map((e) => e.k);
      const years = new Set(values.map((v) => parseDateLabel(v)?.year).filter((y) => y !== undefined));
      const multiYear = years.size > 1;
      const sameDay = new Set(values.map((v) => {
        const d = parseDateLabel(v);
        return d ? `${d.year}-${d.month}-${d.day}` : v;
      })).size < values.length;
      return {
        kind: "date",
        keys: order,
        labelOf: (k) => formatDateLabel(k, { multiYear, withTime: sameDay }),
      };
    }
    const wd = values.map(weekdayKey);
    if (wd.every((k) => k !== null)) {
      const order = keys.map((k, i) => ({ k, key: wd[i] })).sort((a, b) => a.key - b.key).map((e) => e.k);
      return { kind: "weekday", keys: order, labelOf: (k) => k };
    }
  }

  return { kind: "category", keys, labelOf: (k) => k };
}

/**
 * An ISO date ("2026-10-13", "2025-02-23 00:00:23") becomes "13 Oct" (with the year when the
 * axis spans several, with the time when two labels fall on the same day). Any other spelling
 * is shown as the AI wrote it: it may carry a note the date alone would lose.
 */
export function formatDateLabel(label, { multiYear = false, withTime = false } = {}) {
  const match = typeof label === "string" && label.trim().match(ISO_DATE);
  if (!match) return label;
  const month = Number(match[2]) - 1;
  if (month < 0 || month > 11) return label;
  let text = `${Number(match[3])} ${MONTHS_SHORT[month]}`;
  if (multiYear) text += ` ${match[1]}`;
  if (withTime && match[4] !== undefined) text += ` ${match[4].padStart(2, "0")}:${match[5]}`;
  return text;
}

/* ----------------------------------------------------------------------------- series */

const featuresOf = (series) => new Set(Array.isArray(series?.features) ? series.features : []);

const isHex = (value) => typeof value === "string" && /^#[0-9a-f]{6}$/i.test(value.trim());

/** The AI's x/y arrays, zipped to the shorter one, with values coerced and gaps as null. */
function readPoints(series) {
  let xs = Array.isArray(series.x_values) ? series.x_values : [];
  let ys = Array.isArray(series.y_values) ? series.y_values : [];
  const zs = Array.isArray(series.z_values) ? series.z_values : [];
  const colors = Array.isArray(series.color_values) ? series.color_values : [];

  // Horizontal bars written the other way round: numbers in x, categories in y.
  let swapped = false;
  if (series.series_type === "bar" && xs.length && ys.length
    && xs.every((v) => isJsonNumber(v) || toNumber(v) !== null)
    && ys.some((v) => typeof v === "string" && toNumber(v) === null)) {
    [xs, ys] = [ys, xs];
    swapped = true;
  }

  if (xs.length === 0 && ys.length > 0) xs = ys.map((_, i) => `Item ${i + 1}`);
  const count = Math.min(xs.length, ys.length);
  const points = [];
  for (let i = 0; i < count; i += 1) {
    const x = xs[i];
    if (x === null || x === undefined || (typeof x === "string" && x.trim() === "")) continue;
    points.push({
      x,
      key: String(x),
      y: toNumber(ys[i]),
      z: toNumber(zs[i]),
      color: isHex(colors[i]) ? colors[i].trim().toUpperCase() : null,
    });
  }
  points.swapped = swapped;
  return points;
}

/**
 * Series colours: the spec's own, unless missing or already used by an earlier series of the
 * same type. A scatter point deliberately painted like its line keeps that colour.
 */
function assignColors(seriesList) {
  const usedByType = new Map();
  const usedAnywhere = new Set();
  let next = 0;
  const pick = () => {
    while (next < SERIES_COLORS.length && usedAnywhere.has(SERIES_COLORS[next])) next += 1;
    const color = next < SERIES_COLORS.length ? SERIES_COLORS[next] : SERIES_COLORS[(next - SERIES_COLORS.length) % SERIES_COLORS.length];
    next += 1;
    return color;
  };
  seriesList.forEach((series) => {
    if (!usedByType.has(series.type)) usedByType.set(series.type, new Set());
    const used = usedByType.get(series.type);
    const own = isHex(series.specColor) ? series.specColor.trim().toUpperCase() : null;
    series.color = own && !used.has(own) ? own : pick();
    used.add(series.color);
    usedAnywhere.add(series.color);
  });
}

function readSeries(spec) {
  const list = Array.isArray(spec?.series) ? spec.series : [];
  return list
    .filter((s) => s && typeof s === "object")
    .map((s, index) => {
      const type = typeof s.series_type === "string" ? s.series_type.toLowerCase() : "bar";
      const features = featuresOf(s);
      const points = readPoints(s);
      return {
        id: `s${index}`,
        index,
        type: ["line", "area", "bar", "scatter", "pie", "radar"].includes(type) ? type : "bar",
        title: typeof s.title === "string" && s.title.trim() ? s.title.trim() : `Series ${index + 1}`,
        specColor: s.color,
        color: null,
        features,
        showInLegend: s.show_in_legend !== false,
        points,
        swapped: points.swapped === true,
        hasPointColors: points.some((p) => p.color),
        rightAxis: features.has("alt_y_axis"),
        altX: features.has("alt_x_axis"),
        stacked: features.has("stacked"),
        dashed: features.has("dashed"),
        noDots: features.has("no_dots"),
        vertical: features.has("vertical"),
      };
    })
    .filter((s) => s.points.length > 0);
}

/* ----------------------------------------------------------------------------- panels */

function cartesianPanel(seriesList, spec, notes, altXPanel = false) {
  const panel = { kind: "cartesian", series: seriesList, notes: [] };
  const allX = [];
  seriesList.forEach((s) => s.points.forEach((p) => allX.push(p.x)));
  const axis = classifyAxis(allX);
  panel.x = axis;

  const bars = seriesList.filter((s) => s.type === "bar");
  const allBars = bars.length === seriesList.length;
  // Bars lie on their side when asked, or when the category labels are too long to stand;
  // never when some series read against a second value axis, which only columns can show.
  const twoAxes = seriesList.some((s) => s.rightAxis) && seriesList.some((s) => !s.rightAxis);
  panel.horizontal = allBars && !twoAxes && bars.some((s) => s.vertical);

  const labels = axis.keys.map(axis.labelOf);
  const longest = labels.reduce((m, l) => Math.max(m, String(l).length), 0);
  panel.longestLabel = longest;
  if (allBars && !twoAxes && !panel.horizontal && axis.kind === "category") {
    if ((axis.keys.length >= 6 && longest > 18) || longest > 32) panel.horizontal = true;
  }

  // Rows: one per x key, with each series' value under its id.
  const rows = axis.keys.map((key) => ({ key, label: axis.labelOf(key), x: axis.kind === "number" ? Number(key) : key }));
  const rowIndex = new Map(rows.map((row, i) => [row.key, i]));
  seriesList.forEach((s) => {
    s.points.forEach((p) => {
      const row = rows[rowIndex.get(p.key)];
      if (!row) return;
      row[s.id] = p.y;
      if (p.z !== null) row[`${s.id}_z`] = p.z;
      if (p.color) row[`${s.id}_color`] = p.color;
    });
  });
  panel.rows = rows;

  // Stacks per type: stacked bars pile with stacked bars, stacked areas with stacked areas.
  seriesList.forEach((s) => {
    s.stackId = s.stacked && (s.type === "bar" || s.type === "area") ? `stack-${s.type}` : undefined;
  });
  // Bar series that never share a category ("top 5 locations per depot") are one bar per
  // row coloured by series, not a grouped chart with empty slots in every group.
  if (allBars && bars.length > 1 && !bars.some((s) => s.stackId)) {
    const owners = new Map();
    let disjoint = true;
    bars.forEach((s) => s.points.forEach((p) => {
      if (owners.has(p.key) && owners.get(p.key) !== s.id) disjoint = false;
      owners.set(p.key, s.id);
    }));
    if (disjoint) bars.forEach((s) => { s.stackId = "stack-bar"; s.disjoint = true; });
  }

  // One value axis, or two when the spec puts series on the alternative axis.
  const left = seriesList.filter((s) => !s.rightAxis);
  const right = seriesList.filter((s) => s.rightAxis);
  if (left.length === 0) {
    right.forEach((s) => { s.rightAxis = false; });
    left.push(...right.splice(0));
  }
  // The contract describes the axes "as if not flipped": pri_y_axis is the value axis and
  // pri_x_axis the category axis, horizontal bars included. When the agent wrote the
  // numbers in x_values it described the axes the flipped way too, so follow the data.
  const specsSwapped = allBars && bars.every((s) => s.swapped);
  const valueSpec = specsSwapped ? spec?.pri_x_axis : spec?.pri_y_axis;
  const categorySpec = specsSwapped ? spec?.pri_y_axis : (altXPanel ? spec?.alt_x_axis : spec?.pri_x_axis);
  panel.leftAxis = valueAxis(left, rows, valueSpec);
  panel.rightAxis = right.length ? valueAxis(right, rows, spec?.alt_y_axis) : null;

  panel.xTitle = typeof categorySpec?.title === "string" ? categorySpec.title.trim() : "";
  const xSpec = categorySpec;
  if (axis.kind === "number") {
    const numbers = rows.map((r) => r.x);
    panel.xScale = buildScale(numbers, xSpec, { includeZero: false, targetTicks: 6 });
  }

  // Readability: colours, muted tail, value labels.
  const lines = seriesList.filter((s) => s.type === "line" || s.type === "area");
  if (seriesList.length > MAX_COLOURED_SERIES && lines.length === seriesList.length) {
    const ranked = [...seriesList].sort((a, b) => meanOf(b) - meanOf(a));
    const kept = new Set(ranked.slice(0, MAX_COLOURED_SERIES).map((s) => s.id));
    seriesList.forEach((s) => { s.muted = !kept.has(s.id); });
    const muted = seriesList.length - kept.size;
    panel.notes.push(`The ${MAX_COLOURED_SERIES} largest series are coloured; the other ${muted} are drawn in grey and named in the tooltip.`);
  }
  panel.valueLabels = allBars && seriesList.length === 1 && rows.length <= 12;
  const allValues = rows.flatMap((row) => seriesList.map((s) => row[s.id]).filter((v) => v !== null && v !== undefined));
  if (allValues.length && allValues.every((v) => v === 0)) {
    panel.notes.push("Every value in this chart is zero.");
  }
  panel.legend = seriesList.length > 1;
  const grouped = seriesList.length > 1 && !seriesList.every((s) => s.stackId);
  const rowHeight = grouped ? Math.min(90, 16 * seriesList.length + 10) : (rows.length > 30 ? 22 : 34);
  panel.height = panel.horizontal
    ? Math.min(900, Math.max(240, 60 + rows.length * rowHeight))
    : 340;
  notes.forEach((n) => panel.notes.push(n));
  return panel;
}

const meanOf = (series) => {
  const ys = series.points.map((p) => p.y).filter((y) => y !== null);
  return ys.length ? ys.reduce((a, b) => a + b, 0) / ys.length : -Infinity;
};

function valueAxis(seriesList, rows, spec) {
  const values = [];
  const stacks = new Map();
  rows.forEach((row) => {
    stacks.clear();
    seriesList.forEach((s) => {
      const y = row[s.id];
      if (y === null || y === undefined) return;
      if (s.stackId) {
        const current = stacks.get(s.stackId) || { pos: 0, neg: 0 };
        if (y >= 0) current.pos += y; else current.neg += y;
        stacks.set(s.stackId, current);
      } else {
        values.push(y);
      }
    });
    stacks.forEach((sum) => { values.push(sum.pos); values.push(sum.neg); });
  });
  const title = typeof spec?.title === "string" ? spec.title.trim() : "";
  const scale = buildScale(values, spec);
  return { title, unit: unitFromTitle(title), scale, series: seriesList.map((s) => s.id) };
}

/**
 * The colours a pie's slices walk: a single pie takes the app's palette in its usual order; one
 * of several pies starts with its own family (purples, then blues, then greens) so the pies are
 * told apart at a glance, and continues through the rest of the palette rather than repeating.
 */
export function sliceColors(pieIndex, pieCount) {
  if (pieCount <= 1) return SLICE_COLORS;
  const family = PALETTE_FAMILIES[pieIndex % PALETTE_FAMILIES.length];
  return family.concat(SLICE_COLORS.filter((color) => !family.includes(color)));
}

function piePanel(seriesList) {
  const pies = seriesList.map((s, pieIndex) => {
    const palette = sliceColors(pieIndex, seriesList.length);
    const slices = s.points
      .map((p, i) => ({
        name: p.key,
        value: p.y,
        color: p.color || palette[i % palette.length],
      }))
      .filter((slice) => slice.value !== null && slice.value > 0);
    const total = slices.reduce((sum, slice) => sum + slice.value, 0);
    slices.forEach((slice) => { slice.share = total > 0 ? slice.value / total : 0; });
    return { id: s.id, title: s.title, slices, total, empty: slices.length === 0, legend: slices.length <= MAX_LEGEND_ITEMS };
  });
  const sharedCategories = pies.length > 1
    && pies.every((p) => p.slices.length === pies[0].slices.length
      && p.slices.every((slice, i) => slice.name === pies[0].slices[i].name));
  return {
    kind: "pie",
    pies,
    sharedCategories,
    notes: [],
    height: pies.length === 1 ? 360 : 300,
  };
}

function radarPanel(seriesList, spec) {
  const allX = [];
  seriesList.forEach((s) => s.points.forEach((p) => allX.push(p.x)));
  const axis = classifyAxis(allX);
  const rows = axis.keys.map((key) => ({ key, label: axis.labelOf(key) }));
  const rowIndex = new Map(rows.map((row, i) => [row.key, i]));
  const values = [];
  seriesList.forEach((s) => s.points.forEach((p) => {
    const row = rows[rowIndex.get(p.key)];
    if (!row) return;
    row[s.id] = p.y;
    if (p.y !== null) values.push(p.y);
  }));
  return {
    kind: "radar",
    series: seriesList,
    rows,
    scale: buildScale(values, spec?.pri_y_axis, { targetTicks: 4 }),
    unit: unitFromTitle(spec?.pri_y_axis?.title),
    legend: seriesList.length > 1,
    axisLabels: rows.length <= MAX_LEGEND_ITEMS,
    notes: [],
    height: 380,
  };
}

/** The number of distinct x values across a set of series. */
const categoryCount = (seriesList) => {
  const keys = new Set();
  seriesList.forEach((s) => s.points.forEach((p) => keys.add(p.key)));
  return keys.size;
};

/* ----------------------------------------------------------------------------- the model */

/**
 * @param {object} chartConfig the stored `{ type, title, content }` chart
 * @returns {{ title: string, goal: string, panels: object[], empty: boolean }}
 */
export function buildChartModel(chartConfig) {
  const content = chartConfig?.content;
  const title = typeof content?.title === "string" && content.title.trim()
    ? content.title.trim()
    : (typeof chartConfig?.title === "string" ? chartConfig.title.trim() : "");
  const goal = typeof chartConfig?.title === "string" ? chartConfig.title.trim() : "";
  const empty = { title, goal, panels: [], empty: true };
  if (!content || typeof content !== "object") return empty;

  let seriesList;
  try {
    seriesList = readSeries(content);
  } catch (error) {
    return empty;
  }
  if (seriesList.length === 0) return empty;
  assignColors(seriesList);

  const notes = [];
  const byType = new Map();
  seriesList.forEach((s) => {
    if (!byType.has(s.type)) byType.set(s.type, []);
    byType.get(s.type).push(s);
  });

  const cartesianTypes = Array.from(byType.keys()).filter((t) => CARTESIAN.has(t));
  const hasPolar = byType.has("pie") || byType.has("radar");
  const groups = [];
  if (cartesianTypes.length) {
    if (hasPolar && cartesianTypes.length > 1) {
      // "Show it as bar, line, pie and radar": one panel per requested view.
      cartesianTypes.forEach((t) => groups.push({ type: t, series: byType.get(t), label: t }));
    } else {
      const merged = [];
      cartesianTypes.forEach((t) => merged.push(...byType.get(t)));
      merged.sort((a, b) => a.index - b.index);
      // Series on the alternative x axis with their own categories ("NSW drivers" beside
      // "RAD drivers") are a second chart, not extra bars on the first.
      const onAlt = merged.filter((s) => s.altX);
      const onPri = merged.filter((s) => !s.altX);
      if (onAlt.length && onPri.length && categoryCount(onAlt) + categoryCount(onPri) === categoryCount(merged)) {
        groups.push({ type: "cartesian", series: onPri, label: "pri_x" });
        groups.push({ type: "cartesian", series: onAlt, label: "alt_x" });
      } else {
        groups.push({ type: "cartesian", series: merged });
      }
    }
  }
  if (byType.has("pie")) groups.push({ type: "pie", series: byType.get("pie"), label: "pie" });
  if (byType.has("radar")) groups.push({ type: "radar", series: byType.get("radar"), label: "radar" });
  const multiView = groups.length > 1;

  const panels = groups.map((group) => {
    let panel;
    if (group.type === "pie") {
      panel = piePanel(group.series);
    } else if (group.type === "radar") {
      if (categoryCount(group.series) < 3) {
        group.series.forEach((s) => { s.type = "bar"; });
        panel = cartesianPanel(group.series, content, ["Shown as bars: a radar needs at least three axes."]);
      } else {
        panel = radarPanel(group.series, content);
      }
    } else {
      const lineLike = group.series.every((s) => s.type === "line" || s.type === "area");
      const panelNotes = [];
      if (lineLike && categoryCount(group.series) < 2) {
        group.series.forEach((s) => { s.type = "bar"; });
        panelNotes.push("Shown as bars: a line needs at least two points.");
      }
      panel = cartesianPanel(group.series, content, panelNotes, group.label === "alt_x");
    }
    if (group.label === "pri_x" || group.label === "alt_x") {
      const axisSpec = group.label === "alt_x" ? content.alt_x_axis : content.pri_x_axis;
      panel.heading = typeof axisSpec?.title === "string" && axisSpec.title.trim() ? axisSpec.title.trim() : undefined;
      panel.xTitle = "";
    } else if (multiView && group.label) {
      panel.heading = viewHeading(group.label);
    }
    return panel;
  });

  notes.forEach((n) => panels[0]?.notes.push(n));
  return { title, goal, panels, empty: panels.length === 0 };
}

const viewHeading = (type) => ({
  bar: "Bar view", line: "Line view", area: "Area view", scatter: "Scatter view", pie: "Pie view", radar: "Radar view",
})[type] || type;
